import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

const BulkQuestionSelector = ({
    visible,
    exam,
    questionSelection,
    questionFilters,
    chapters,
    topics,
    availableQuestions,
    selectedBulkQuestions,
    isLoadingQuestions,
    onHide,
    onQuestionSelectionChange,
    onFilterChange,
    onSelectionChange,
    onAddQuestions,
    getSelectedQuestionCount,
    getRequiredQuestionCount,
    fetchQuestionsForSection,
    onPreviewQuestion
}) => {
    return (
        <Dialog
            header="Select Questions"
            visible={visible}
            style={{ width: '90vw' }}
            modal
            onHide={onHide}
            footer={
                <div>
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={onHide}
                        className="p-button-text"
                    />
                    <Button
                        label="Add Selected"
                        icon="pi pi-check"
                        onClick={onAddQuestions}
                        autoFocus
                        disabled={!selectedBulkQuestions.length}
                    />
                </div>
            }
        >
            <div className="grid">
                <div className="col-12 md:col-4">
                    {/* Subject Selection */}
                    <div className="p-field mb-3">
                        <label htmlFor="subject-select" className="block font-medium mb-2">Subject</label>
                        <Dropdown
                            id="subject-select"
                            value={questionSelection.subjectId}
                            options={exam?.subjects || []}
                            optionLabel="subjectName"
                            optionValue="subjectId"
                            onChange={(e) => onQuestionSelectionChange('subjectId', e.value)}
                            placeholder="Select Subject"
                            className="w-full"
                        />
                    </div>

                    {/* Section Selection */}
                    <div className="p-field mb-3">
                        <label htmlFor="section-select" className="block font-medium mb-2">Section</label>
                        <Dropdown
                            id="section-select"
                            value={questionSelection.sectionName}
                            options={Array.isArray(exam?.subjects)
                                ? (exam.subjects.find(s => s.subjectId === questionSelection.subjectId)?.sections || [])
                                : []}
                            optionLabel="sectionName"
                            optionValue="sectionName"
                            onChange={(e) => onQuestionSelectionChange('sectionName', e.value)}
                            placeholder="Select Section"
                            className="w-full"
                        />
                    </div>

                    {/* Question Type Selection */}
                    <div className="p-field mb-3">
                        <label htmlFor="questionType-select" className="block font-medium mb-2">Question Type</label>
                        <Dropdown
                            id="questionType-select"
                            value={questionSelection.questionType}
                            options={Array.isArray(exam?.subjects)
                                ? (exam.subjects.find(s => s.subjectId === questionSelection.subjectId)
                                    ?.sections?.find(sec => sec.sectionName === questionSelection.sectionName)
                                    ?.markingScheme?.map(m => ({ label: m.questionType, value: m.questionType })) || [])
                                : []}
                            onChange={(e) => onQuestionSelectionChange('questionType', e.value)}
                            placeholder="Select Question Type"
                            className="w-full"
                        />
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-3">
                        <label className="block font-medium mb-2">Selection Progress</label>
                        <ProgressBar
                            value={
                                (getSelectedQuestionCount(
                                    questionSelection.subjectId,
                                    questionSelection.sectionName,
                                    questionSelection.questionType
                                ) / Math.max(1, getRequiredQuestionCount(
                                    questionSelection.subjectId,
                                    questionSelection.sectionName,
                                    questionSelection.questionType
                                ))) * 100
                            }
                            showValue={false}
                        />
                        <div className="flex justify-content-between">
                            <span className="font-medium">
                                Selected: {getSelectedQuestionCount(
                                    questionSelection.subjectId,
                                    questionSelection.sectionName,
                                    questionSelection.questionType
                                )}
                            </span>
                            <span className="font-medium">
                                Required: {getRequiredQuestionCount(
                                    questionSelection.subjectId,
                                    questionSelection.sectionName,
                                    questionSelection.questionType
                                )}
                            </span>
                        </div>
                    </div>

                    {/* Filters Section */}
                    <div className="p-card p-3 border-1 surface-border border-round">
                        <h5 className="mt-0">Additional Filters</h5>

                        <div className="p-field mb-3">
                            <label htmlFor="chapter-select" className="block font-medium mb-2">Chapter</label>
                            <Dropdown
                                id="chapter-select"
                                value={questionFilters.chapterId}
                                options={chapters || []}
                                optionLabel="chapterName"
                                optionValue="chapterId"
                                onChange={(e) => onFilterChange('chapterId', e.value)}
                                placeholder="Select Chapter"
                                className="w-full"
                            />
                        </div>

                        <div className="p-field mb-3">
                            <label htmlFor="topic-select" className="block font-medium mb-2">Topic</label>
                            <Dropdown
                                id="topic-select"
                                value={questionFilters.topicId}
                                options={topics || []}
                                optionLabel="topicName"
                                optionValue="topicId"
                                onChange={(e) => onFilterChange('topicId', e.value)}
                                placeholder="Select Topic"
                                className="w-full"
                                disabled={!questionFilters.chapterId}
                            />
                        </div>

                        <div className="p-field">
                            <label htmlFor="difficulty-select" className="block font-medium mb-2">Difficulty</label>
                            <Dropdown
                                id="difficulty-select"
                                value={questionFilters.difficulty}
                                options={[
                                    { label: 'Easy', value: 'Easy' },
                                    { label: 'Medium', value: 'Medium' },
                                    { label: 'Hard', value: 'Hard' }
                                ]}
                                onChange={(e) => onFilterChange('difficulty', e.value)}
                                placeholder="Select Difficulty"
                                className="w-full"
                            />
                        </div>

                        <Button
                            label="Apply Filters"
                            icon="pi pi-filter"
                            className="w-full mt-3"
                            onClick={fetchQuestionsForSection}
                        />
                    </div>
                </div>

                {/* Questions Table Section */}
                <div className="col-12 md:col-8">
                    {isLoadingQuestions ? (
                        <div className="flex align-items-center justify-content-center" style={{ height: '400px' }}>
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                        </div>
                    ) : availableQuestions.length === 0 ? (
                        <div className="flex align-items-center justify-content-center flex-column" style={{ height: '400px' }}>
                            <i className="pi pi-search" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
                            <p className="text-center">No questions found matching your criteria.</p>
                        </div>
                    ) : (
                        <>
                            <div className="mb-3">
                                <Message
                                    severity="info"
                                    text={`Found ${availableQuestions.length} questions. You can select up to ${Math.min(
                                        availableQuestions.length,
                                        getRequiredQuestionCount(
                                            questionSelection.subjectId,
                                            questionSelection.sectionName,
                                            questionSelection.questionType
                                        ) - getSelectedQuestionCount(
                                            questionSelection.subjectId,
                                            questionSelection.sectionName,
                                            questionSelection.questionType
                                        )
                                    )} questions.`}
                                />
                            </div>

                            <DataTable
                                value={availableQuestions}
                                selection={selectedBulkQuestions}
                                onSelectionChange={(e) => onSelectionChange(e.value)}
                                dataKey="questionId"
                                paginator
                                rows={10}
                                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                className="p-datatable-sm"
                                selectionMode="multiple"
                                emptyMessage="No questions found"
                                resizableColumns
                                columnResizeMode="fit"
                            >
                                <Column
                                    selectionMode="multiple"
                                    headerStyle={{ width: '2rem', textAlign: 'center' }}
                                    // headerStyle={{}}
                                    bodyStyle={{ textAlign: 'center' }}
                                    style={{ width: '2rem' }}
                                />
                                <Column
                                    field="questionId"
                                    header="ID"
                                    headerStyle={{ textAlign: 'center' }}
                                    bodyStyle={{ textAlign: 'center' }}
                                    sortable
                                    style={{ width: '8rem' }}
                                />
                                <Column
                                    header="Preview"
                                    headerStyle={{ textAlign: 'center' }}
                                    bodyStyle={{ textAlign: 'center' }}
                                    style={{ width: '5rem' }}
                                    body={(rowData) => (
                                        <span
                                            data-pr-tooltip="Preview Question"
                                            data-pr-position="bottom"
                                            style={{ cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();  // Stop event from bubbling up to row
                                                onPreviewQuestion(rowData);
                                            }}
                                        >
                                            <i className="pi pi-eye" style={{ fontSize: '1.2rem', color: '#2196F3' }}></i>
                                        </span>
                                    )}
                                />
                                <Column
                                    field="difficulty"
                                    header="Difficulty"
                                    headerStyle={{ textAlign: 'center' }}
                                    bodyStyle={{ textAlign: 'center' }}
                                    sortable
                                    style={{ width: '8rem' }}
                                />
                            </DataTable>
                        </>
                    )}
                </div>
            </div>
        </Dialog>
    );
};

export default BulkQuestionSelector;